// Dicionário de constraints de unicidade para mensagens amigáveis
const constraintMessages: Record<string, string> = {
  // Orçamentos
  'quotations_company_id_quotation_number_key': 'Número de orçamento já está sendo utilizado',
  'quotations_company_quotation_number_unique': 'Número de orçamento já está sendo utilizado',
  
  // Vendas
  'sales_company_id_sale_number_key': 'Número de venda já está sendo utilizado',
  'sales_company_sale_number_unique': 'Número de venda já está sendo utilizado',
  
  // Locações
  'rentals_company_id_rental_number_key': 'Número de locação já está sendo utilizado',
  'rentals_company_rental_number_unique': 'Número de locação já está sendo utilizado',
  
  // Compras
  'purchases_company_id_purchase_number_key': 'Número de compra já está sendo utilizado',
  
  // Notas Fiscais
  'invoices_access_key_key': 'Chave de acesso da nota fiscal já existe',
  'invoices_company_number_series_key': 'Número e série da nota fiscal já existem',
  
  // Clientes
  'customers_company_id_document_number_key': 'Já existe um cliente com este documento (CPF/CNPJ)',
  
  // Produtos
  'products_company_id_sku_key': 'SKU do produto já está sendo utilizado',
  
  // Fornecedores
  'suppliers_company_id_document_number_key': 'Já existe um fornecedor com este documento (CPF/CNPJ)',
  
  // Empréstimos
  'loans_company_loan_number_unique': 'Número do empréstimo já está sendo utilizado',
  
  // Drafts
  'sales_drafts_unique_per_user_entity': 'Já existe um rascunho em andamento para este documento',
  'sales_drafts_upsert_key': 'Já existe um rascunho em andamento para este documento.',
};

const stageLabels: Record<string, string> = {
  'validate_data': 'validação dos dados',
  'check_credit': 'verificação de crédito',
  'insert_sale': 'criação da venda',
  'insert_items': 'itens da venda',
  'insert_payments': 'pagamentos',
  'register_stock': 'movimentação de estoque',
  'financial_auto': 'registro financeiro',
  'update_session_totals': 'atualização do caixa',
};

const tableNames: Record<string, string> = {
  'sale_payments': 'pagamentos',
  'sales': 'vendas',
  'sale_items': 'itens de venda',
  'sale_installments': 'parcelas de venda',
  'customers': 'clientes',
  'products': 'produtos',
  'stock_movements': 'movimentações de estoque',
  'pos_sessions': 'sessões de caixa',
  'pos_cash_movements': 'movimentações de caixa',
  'accounts_receivable': 'contas a receber',
  'accounts_payable': 'contas a pagar',
  'invoices': 'notas fiscais',
  'invoice_items': 'itens de nota fiscal',
  'rentals': 'locações',
  'deliveries': 'entregas',
  'quotations': 'orçamentos',
  'purchases': 'compras',
  'expenses': 'despesas',
  'revenues': 'receitas',
  'receivable_payments': 'pagamentos de recebíveis',
  'suppliers': 'fornecedores',
};

const columnNames: Record<string, string> = {
  'customer_id': 'cliente',
  'company_id': 'empresa',
  'sale_id': 'venda',
  'product_id': 'produto',
  'payment_method': 'forma de pagamento',
  'payment_date': 'data de pagamento',
  'payment_value': 'valor do pagamento',
  'quantity': 'quantidade',
  'unit_price': 'preço unitário',
  'total_value': 'valor total',
  'subtotal': 'subtotal',
  'total': 'total',
  'due_date': 'data de vencimento',
  'issue_date': 'data de emissão',
  'sale_date': 'data da venda',
  'description': 'descrição',
  'transaction_number': 'número da transação',
  'original_value': 'valor original',
  'sale_number': 'número da venda',
  'status': 'status',
  'salesperson_id': 'vendedor',
  'created_by': 'criado por',
  'financial_account_id': 'conta financeira',
  'receivable_id': 'conta a receber',
  'expense_category_id': 'categoria da despesa',
  'income_category_id': 'categoria da receita',
  'supplier_id': 'fornecedor',
  'cost_center_id': 'centro de custo',
  'payable_id': 'conta a pagar',
};

function parseNotNullConstraint(message: string, details?: string): { column?: string; table?: string } {
  // Pattern: null value in column "X" of relation "Y" violates not-null constraint
  const pattern1 = /null value in column ["']?(\w+)["']? of relation ["']?(\w+)["']?/i;
  const match1 = message.match(pattern1) || details?.match(pattern1);
  if (match1) {
    return { column: match1[1], table: match1[2] };
  }

  // Pattern: column "X" of relation "Y" contains null values
  const pattern2 = /column ["']?(\w+)["']? of relation ["']?(\w+)["']? contains null/i;
  const match2 = message.match(pattern2) || details?.match(pattern2);
  if (match2) {
    return { column: match2[1], table: match2[2] };
  }

  // Pattern: "X" violates not-null constraint
  const pattern3 = /["']?(\w+)["']? violates not-null/i;
  const match3 = message.match(pattern3) || details?.match(pattern3);
  if (match3) {
    return { column: match3[1] };
  }

  return {};
}

export function translateSupabaseError(error: any): string {
  const message = error?.message || String(error);
  const details = error?.details || error?.hint || '';
  const stage = error?.__stage;
  
  // Prefix with stage if available
  const stagePrefix = stage && stageLabels[stage] 
    ? `[${stageLabels[stage]}] ` 
    : '';
  
  // Erros de RLS
  if (message.includes('row-level security policy') || message.includes('violates row-level security')) {
    const tableMatch = message.match(/table ["']?(\w+)["']?/);
    const table = tableMatch?.[1];
    const tableName = table ? tableNames[table] || table : 'dados';
    return `${stagePrefix}Sem permissão para acessar ${tableName}. Contate o administrador.`;
  }
  
  // Erros de autenticação
  if (message.includes('JWT expired')) {
    return 'Sua sessão expirou. Faça login novamente.';
  }
  
  if (message.includes('not authenticated') || message.includes('Invalid login')) {
    return 'Você precisa estar logado para realizar esta ação.';
  }

  // §I.146: ban de Auth (usuário suspenso/inativado pelo ciclo de vida)
  if (message.toLowerCase().includes('banned')) {
    return 'Perfil suspenso ou inativado. Contate o administrador.';
  }

  // Erros de sessão e link de recuperação
  if (message.includes('Auth session missing')) {
    return 'Sessão expirada. O link de recuperação não é mais válido. Solicite um novo link.';
  }

  if (message.includes('Email link is invalid or has expired') || message.includes('otp_expired')) {
    return 'O link de recuperação expirou ou é inválido. Solicite um novo link.';
  }

  // Erros de senha
  if (message.includes('Password should contain at least one character of each')) {
    return 'A senha deve conter pelo menos: uma letra minúscula, uma letra maiúscula, um número e um caractere especial (!@#$%^&*).';
  }

  if (message.includes('Password should contain at least one')) {
    // Erros mais específicos de caracteres
    if (message.includes('uppercase')) {
      return 'A senha deve conter pelo menos uma letra maiúscula.';
    }
    if (message.includes('lowercase')) {
      return 'A senha deve conter pelo menos uma letra minúscula.';
    }
    if (message.includes('digit') || message.includes('number')) {
      return 'A senha deve conter pelo menos um número.';
    }
    if (message.includes('special')) {
      return 'A senha deve conter pelo menos um caractere especial (!@#$%^&*).';
    }
    return 'A senha não atende aos requisitos de segurança.';
  }

  if (message.includes('Password should be at least')) {
    const match = message.match(/at least (\d+) characters/);
    const chars = match?.[1] || '6';
    return `A senha deve ter no mínimo ${chars} caracteres.`;
  }

  if (message.includes('New password should be different')) {
    return 'A nova senha deve ser diferente da senha atual.';
  }

  if (message.includes('Password is too weak') || message.includes('password is too weak')) {
    return 'A senha é muito fraca. Use uma combinação de letras maiúsculas, minúsculas, números e caracteres especiais.';
  }
  
  // Erros de constraint de unicidade
  if (message.includes('violates unique constraint')) {
    // Tentar extrair nome específico da constraint
    const constraintMatch = message.match(/["']([^"']+)["']/);
    if (constraintMatch) {
      const constraintName = constraintMatch[1];
      const friendlyMessage = constraintMessages[constraintName];
      if (friendlyMessage) {
        return `${stagePrefix}${friendlyMessage}`;
      }
    }
    // Fallback genérico
    return `${stagePrefix}Este registro já existe no sistema.`;
  }
  
  if (message.includes('violates foreign key constraint')) {
    return `${stagePrefix}Registro relacionado não encontrado.`;
  }

  if (message.includes('violates not-null constraint') || message.includes('null value in column')) {
    const { column, table } = parseNotNullConstraint(message, details);
    
    if (column && table) {
      const columnLabel = columnNames[column] || column;
      const tableLabel = tableNames[table] || table;
      return `${stagePrefix}Campo obrigatório não preenchido: ${columnLabel} (tabela: ${tableLabel}).`;
    } else if (column) {
      const columnLabel = columnNames[column] || column;
      return `${stagePrefix}Campo obrigatório não preenchido: ${columnLabel}.`;
    }
    
    return `${stagePrefix}Campos obrigatórios não foram preenchidos.`;
  }

  // Erro de trigger - campo inexistente no record (ex: "has no field 'payment_date'" ou "has no field 'total_value'")
  if (message.includes('has no field')) {
    // Extrair nome do campo para código de erro rastreável
    const fieldMatch = message.match(/has no field ['"]?(\w+)['"]?/);
    const fieldName = fieldMatch?.[1];
    const errorCode = fieldName ? `FIELD_MISMATCH_${fieldName.toUpperCase()}` : 'FIELD_MISMATCH';
    
    return `${stagePrefix}Erro interno ao processar o registro. Ocorreu uma incompatibilidade no sistema. Por favor, aguarde alguns segundos e tente novamente. Se o problema persistir, contate o suporte informando o código: ${errorCode}.`;
  }
  
  // Erros de schema cache (coluna inexistente no PostgREST)
  if (message.includes('schema cache') || message.includes('Could not find')) {
    return `${stagePrefix}Erro de configuração interna. O sistema está sendo atualizado. Tente novamente em alguns instantes ou contate o suporte.`;
  }

  // Erros de conexão
  if (message.includes('network') || message.includes('Failed to fetch')) {
    return 'Erro de conexão. Verifique sua internet.';
  }
  
  // Retorna a mensagem original com prefixo de stage se disponível
  return stagePrefix ? `${stagePrefix}${message}` : message;
}
